import styled, { css } from 'styled-components';
import { Radio } from '@lmn/design-system';
import { starfishCssUrl } from '@/app/common/icons/starfish';

export const SwitcherContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  margin-bottom: 16px;
  padding: ${({ theme }) => theme.kallisto.space.space200};
  border: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
  border-radius: ${({ theme }) => theme.kallisto.border.radius.radius200};
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer02};
`;

export const SwitcherInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

export const SwitcherIcon = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundNeutralSubtle};
`;

export const SwitcherTitle = styled.div`
  ${({ theme }) => theme.kallisto.mixins.font('Title5')};
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
`;

export const SwitcherCaption = styled.div`
  ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
  color: ${({ theme }) => theme.kallisto.color.text.static.textSecondary};

  b {
    color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
    font-weight: ${({ theme }) => theme.kallisto.font.weight.bold};
  }
`;

export const SwitcherOptions = styled.div`
  display: flex;
  flex-shrink: 0;
  gap: 8px;
`;

/**
 * Same visual language as the horizontal filter pills (see Filters/HorizontalFilters/Pill/styled.ts):
 * 40px tall, 19px radius, neutral border, brand ring on hover and a brand-tinted selected state. The
 * filter pill component itself is welded to the filter machinery, and its container carries filter-bar
 * layout rules (a third of the width on mobile, dividers), so only the styling is shared.
 */
export const OptionPill = styled.button<{ selected: boolean }>`
  display: flex;
  align-items: center;
  height: 40px;
  padding: ${({ theme }) => `${theme.kallisto.space.space100} ${theme.kallisto.space.space150}`};
  border: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
  border-radius: 19px;
  background-color: ${({ theme }) => theme.kallisto.color.common.white[1000]};
  ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  white-space: nowrap;
  cursor: pointer;
  transition: box-shadow 0.2s ease-out;

  &:hover {
    border-color: transparent;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.kallisto.color.background.static.backgroundBrandPrimary};
    color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault};
  }

  ${({ selected, theme }) =>
    selected &&
    css`
      background-color: ${theme.kallisto.color.background.interactive.backgroundPrimarySelected};
      border-color: ${theme.kallisto.color.border.interactive.borderPrimaryDefault};
      box-shadow: 0 0 0 1.5px ${theme.kallisto.color.background.static.backgroundLayer01};
      color: ${theme.kallisto.color.text.interactive.textPrimaryDefault};
    `};
`;

export const CollapsedRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  height: 44px;
  margin-bottom: 16px;
  padding: 12px 16px;
  border: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
  border-radius: 8px;
  background-color: ${({ theme }) => theme.kallisto.color.common.white[1000]};
  cursor: pointer;
`;

export const CollapsedRowLabel = styled.span`
  ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
`;

export const CollapsedRowValue = styled.span`
  ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault};
`;

/**
 * The chevron is masked rather than drawn as an `<img>`: the asset carries a hardcoded near-black
 * fill, so it cannot follow the brand colour any other way (same approach as the Evo pager's
 * chevron). Rotated a quarter turn to point down, matching the collapsed select it sits in.
 */
export const CollapsedRowChevron = styled.span`
  display: block;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  background-color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault};
  mask-image: url('${starfishCssUrl('Arrows', 'icon-chevron-right')}');
  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: 10px 10px;
  -webkit-mask-image: url('${starfishCssUrl('Arrows', 'icon-chevron-right')}');
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
  -webkit-mask-size: 10px 10px;
  transform: rotate(90deg);
`;

export const SheetSubtitle = styled.div`
  ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
  padding: 4px 0 8px;
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  text-align: center;
`;

// No horizontal inset of its own: the modal body already provides it, and adding another doubled the
// left gap and pulled every divider narrower than the subtitle above them and the Apply button below.
export const SheetOptions = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderDisabled};
`;

/**
 * One option per row, radio and label on the left, the departure window right-aligned — the DS
 * `secondaryLabel` slot stacks the secondary text under the label instead, and its root label is
 * inline-flex, which lets two options share a line.
 */
export const SheetOption = styled(Radio)`
  width: 100%;
  min-height: 48px;
  padding: 12px 0;
  border-radius: 0;

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderDisabled};
  }

  [data-testid='radio-label'] {
    flex: 1;
    margin-left: 12px;
  }
`;

export const SheetOptionRow = styled.span`
  ${({ theme }) => theme.kallisto.mixins.font('Body')};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  font-weight: ${({ theme }) => theme.kallisto.font.weight.bold};
`;

export const SheetOptionWindow = styled.span`
  ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
  color: ${({ theme }) => theme.kallisto.color.text.static.textSecondary};
  text-align: right;
  white-space: nowrap;

  b {
    color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
    font-weight: ${({ theme }) => theme.kallisto.font.weight.bold};
  }
`;
